import { AnimatePresence, motion } from 'framer-motion'
import { useState, useEffect } from 'react'
import { MuseWSMessage, MuseWSMessage_RoomReaction } from '@/pages/CollaboratePage/Types'
import { wsConnection } from '@/services/WSConnectionInstance'

interface ReactionDisplayProps {
    userUrn: string | 'viewer'
    inline?: boolean
}

const REACTION_ANIMATION_VARIANTS = {
    wiggleUp: {
        variants: {
            initial: {
                scale: 0,
                opacity: 0,
                y: 0,
            },
            animate: {
                scale: [0, 1.2, 1],
                opacity: [0, 1, 1, 0],
                y: [0, -40, -80],
                rotate: [0, -10, 10, -5, 0],
                transition: {
                    duration: 2,
                    times: [0, 0.1, 0.2, 1],
                    ease: 'easeOut',
                },
            },
            exit: {
                opacity: 0,
                scale: 0,
                transition: {
                    duration: 0.5,
                },
            },
        },
        timoutMs: 3_000,
    },
    basketball: {
        variants: {
            initial: {
                scale: 0,
                opacity: 0,
                y: 0,
            },
            animate: {
                scale: [0, 1.2, 0.8, 1.1, 0.9, 1.05, 0.95, 1],
                opacity: [0, 1, 1, 1, 1, 1, 1, 0],
                y: [
                    0, // Start
                    -80, // First big bounce (drop from high)
                    0, // Hit ground
                    -50, // Second bounce
                    0, // Hit ground
                    -25, // Third bounce
                    0, // Hit ground
                    -10, // Final small bounce
                    0, // Rest
                ],
                transition: {
                    duration: 2.2,
                    times: [0, 0.15, 0.25, 0.45, 0.55, 0.75, 0.82, 0.92, 1],
                    ease: [0.25, 0.46, 0.45, 0.94], // Custom cubic-bezier for realistic bounce
                },
            },
            exit: {
                opacity: 0,
                scale: 0.8,
                transition: {
                    duration: 0.3,
                },
            },
        },
        timoutMs: 3_000,
    },
    tornado: {
        variants: {
            initial: {
                scale: 0,
                opacity: 0,
                x: 0,
                y: 0,
                rotate: 0,
            },
            animate: {
                scale: [0, 1.2, 1.5, 0.8, 1.8, 0.5],
                opacity: [0, 1, 1, 1, 0.8, 0],
                x: [
                    0, // Start center
                    20, // Small circle
                    -30, // Expand
                    60, // Wider
                    -80, // Even wider
                    120, // Flung out
                ],
                y: [
                    0, // Start
                    -40, // Rise
                    -100, // Higher
                    -180, // Much higher
                    -300, // Way up
                    -450, // Off screen
                ],
                rotate: [0, 180, 540, 1080, 1800, 2520], // Multiple full spins
                transition: {
                    duration: 3.5,
                    times: [0, 0.15, 0.35, 0.55, 0.75, 1],
                    ease: [0.25, 0.46, 0.45, 0.94],
                },
            },
            exit: {
                opacity: 0,
                scale: 0,
                transition: {
                    duration: 0.2,
                },
            },
        },
        timoutMs: 3_700,
    },
}

type AnimatedReaction = {
    emoji: string
    id: number
    x: number
}

const ReactionOverlay: React.FC<ReactionDisplayProps> = ({ userUrn }) => {
    const newReaction = useReactionListener(userUrn)
    const [reactionId, setReactionId] = useState(0)
    const [reactions, setReactions] = useState<AnimatedReaction[]>([])
    const reactionAnimationVariants = REACTION_ANIMATION_VARIANTS.wiggleUp

    useEffect(() => {
        if (newReaction) {
            setReactions((prev) => [
                ...prev,
                {
                    id: reactionId,
                    emoji: newReaction.reaction,
                    x: Math.random() * 250,
                },
            ])
            setReactionId((prev) => prev + 1)

            // Remove reaction after animation completes
            setTimeout(() => {
                setReactions((prev) => prev.filter((r) => r.id !== reactionId))
            }, reactionAnimationVariants.timoutMs)
        }
    }, [newReaction])

    return reactions.length > 0 ? (
        <AnimatePresence>
            {reactions.map((reaction) => (
                <motion.div
                    key={reaction.id}
                    className="absolute bottom-0 z-19 pointer-none text-4xl"
                    style={{
                        right: reaction.x,
                    }}
                    variants={reactionAnimationVariants.variants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                >
                    {reaction.emoji}
                </motion.div>
            ))}
        </AnimatePresence>
    ) : null
}

interface ReactionWithTimestamp {
    reaction: string
    timestamp: number
}

const useReactionListener = (userUrn: string | 'viewer') => {
    const [currentReaction, set_currentReaction] = useState<ReactionWithTimestamp | null>(null)

    useEffect(() => {
        let reactionTimeout: NodeJS.Timeout | null = null
        const reactionHandler = (msg: MuseWSMessage) => {
            if (msg.type === 'room-reaction') {
                const { data } = msg as MuseWSMessage_RoomReaction
                if (data.senderUrn === userUrn || (userUrn === 'viewer' && data.clientType === 'listenonly')) {
                    const { body } = data
                    set_currentReaction({
                        reaction: body,
                        timestamp: Date.now(),
                    })
                    if (reactionTimeout) {
                        clearTimeout(reactionTimeout)
                    }
                    reactionTimeout = setTimeout(() => {
                        set_currentReaction(null)
                    }, 5000)
                }
            }
        }
        wsConnection.addEventListener('onmessage', reactionHandler)
        return () => {
            wsConnection.removeEventListener('onmessage', reactionHandler)
            if (reactionTimeout) {
                clearTimeout(reactionTimeout)
            }
        }
    }, [userUrn])

    return currentReaction
}

export default ReactionOverlay
